// frontend/src/GothamPosProducts/GothamPosProducts.jsx — /gotham-pos-products: choose which Gotham FC Shop products are on the
// Point of Sale channel, and move stock from Eatontown Warehouse (Gotham FC) to SI Stadium.
// Mockup: https://claude.ai/artifact/A3LFMccThcEpwz5awWysY8
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { gppApi } from './apiClient';

const TABS = [['on', 'On POS'], ['off', 'Not on POS'], ['all', 'All products']];

const num = (n) => (Number(n) || 0).toLocaleString('en-US');
const plural = (n, w) => `${num(n)} ${w}${n === 1 ? '' : 's'}`;
const fmtDate = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
const sum = (p, k) => p.variants.reduce((a, v) => a + (v[k] || 0), 0);
const avail = (v) => (v.blocked ? 0 : v.eatontown);
const names = (list) => list.slice(0, 3).map((p) => p.title).join(', ') + (list.length > 3 ? ` and ${list.length - 3} more` : '');

function Seg({ value, onChange }) {
  return (
    <div className="uh-gc-seg" role="radiogroup" aria-label="Show">
      {TABS.map(([v, text]) => <button key={v} type="button" role="radio" aria-checked={value === v} onClick={() => onChange(v)}>{text}</button>)}
    </div>
  );
}

function Thumb({ src, small }) {
  return (
    <div className={`uh-gpp-img${small ? ' sm' : ''}`}>
      {src ? <img src={src} alt="" loading="lazy" /> : <span>No image</span>}
    </div>
  );
}

function Transfer({ product, from, to, canWrite, onClose, onDone }) {
  const [qtys, setQtys] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [busy, onClose]);

  const set = (id, n) => { setQtys((q) => ({ ...q, [id]: n })); setConfirming(false); setError(''); };
  const total = product.variants.reduce((a, v) => a + (qtys[v.id] || 0), 0);
  const valid = product.variants.every((v) => { const n = qtys[v.id] || 0; return n >= 0 && n <= avail(v); });
  const maxAll = product.variants.reduce((a, v) => a + avail(v), 0);

  const go = async () => {
    if (!confirming) { setConfirming(true); return; }
    setBusy(true); setError('');
    try {
      const moves = product.variants.filter((v) => qtys[v.id] > 0).map((v) => ({ variant_id: v.id, qty: qtys[v.id] }));
      onDone(await gppApi.transfer(product.id, moves));
    } catch (e) {
      setError(e.message); setConfirming(false);
    }
    setBusy(false);
  };

  return (
    <>
      <div className="uh-gpp-scrim" onClick={() => !busy && onClose()} />
      <aside className="uh-gpp-drawer" role="dialog" aria-modal="true" aria-label={`Transfer ${product.title} to ${to}`}>
        <div className="hd">
          <Thumb src={product.image} small />
          <div className="ttl">
            <h2>Transfer to {to}</h2>
            <div className="route"><b>{product.title}</b></div>
            <div className="route">{from} <span aria-hidden="true">→</span> <b>{to}</b></div>
          </div>
          <button type="button" className="uh-btn slim" onClick={onClose} disabled={busy}>Close</button>
        </div>
        <div className="bd">
          <div className="uh-gpp-all">
            <span><b className="uh-num">{num(maxAll)}</b> {maxAll === 1 ? 'unit' : 'units'} available at Eatontown</span>
            <button type="button" className="uh-btn primary slim" disabled={!maxAll || busy}
              onClick={() => { const q = {}; product.variants.forEach((v) => { q[v.id] = avail(v); }); setQtys(q); setConfirming(false); setError(''); }}>
              Transfer all inventory
            </button>
          </div>
          <table className="uh-tbl">
            <thead><tr><th>Size</th><th className="r">Eatontown</th><th className="r">Stadium</th><th className="r">Move</th></tr></thead>
            <tbody>
              {product.variants.map((v) => {
                const n = qtys[v.id] || 0; const a = avail(v); const over = n > a;
                return (
                  <tr key={v.id}>
                    <td><b>{v.title}</b><div className="sm uh-mono">{v.sku}</div></td>
                    <td className={`r uh-num${v.eatontown < 0 ? ' uh-gpp-neg' : ''}`}>{v.eatontown == null ? '—' : num(v.eatontown)}</td>
                    <td className="r uh-num">{v.stadium == null ? '—' : num(v.stadium)}{n > 0 && !over && <span className="sm"> → {num((v.stadium || 0) + n)}</span>}</td>
                    <td>
                      {a > 0 ? (
                        <>
                          <div className="uh-gpp-move">
                            <input className={`uh-gpp-qty${over ? ' bad' : ''}`} type="number" min="0" max={a} step="1" placeholder="0"
                              value={n || ''} disabled={busy} aria-label={`Units of ${v.title} to move`}
                              onChange={(e) => set(v.id, Math.max(0, Math.floor(Number(e.target.value) || 0)))} />
                            <button type="button" className="uh-btn slim" disabled={n === a || busy} title={`Move all ${a} of ${v.title}`} onClick={() => set(v.id, a)}>All</button>
                          </div>
                          {over && <div className="uh-gpp-why">Only {num(a)} available at Eatontown</div>}
                        </>
                      ) : <div className="uh-gpp-why">{v.blocked}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="uh-gpp-hint">Counts change right away: Eatontown goes down and {to} goes up by the same number, the same way BOPIS step 4 moves stock. No transfer record is created. Sizes at zero or below at Eatontown are blocked, and the reason shows next to each one.</p>
        </div>
        <div className="ft">
          {error && <div className="uh-kpc-banner bad">{error}</div>}
          {!canWrite && <div className="uh-kpc-banner warn">Transfers only run on the production Hub.</div>}
          {confirming && (
            <div className="uh-kpc-banner warn">
              Move <b>{num(total)}</b> {total === 1 ? 'unit' : 'units'} of {product.title} from {from} to {to} now? This changes both locations' counts in Shopify.
            </div>
          )}
          <div className="row">
            <button type="button" className="uh-btn" disabled={busy} onClick={() => { if (confirming) setConfirming(false); else { setQtys({}); setError(''); } }}>
              {confirming ? 'Back' : 'Clear'}
            </button>
            <button type="button" className="uh-btn primary" disabled={!valid || total <= 0 || busy || !canWrite} onClick={go}>
              {busy ? 'Moving…' : confirming ? `Move ${num(total)} ${total === 1 ? 'unit' : 'units'}` : `Review transfer (${num(total)})`}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

export default function GothamPosProducts() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('on');
  const [q, setQ] = useState('');
  const [coll, setColl] = useState('');
  const [sel, setSel] = useState(() => new Set());
  const [asking, setAsking] = useState(null);       // 'add' | 'rem' while the confirmation shows
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);       // { tone, text }
  const [xfer, setXfer] = useState(null);           // product id in the transfer panel

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await gppApi.products()); setError(''); } catch (e) { setError(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const products = useMemo(() => (data ? data.products : []), [data]);
  const collections = useMemo(() => [...new Set(products.flatMap((p) => p.collections))].sort(), [products]);
  const shown = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return products.filter((p) => (tab === 'all' || (tab === 'on' ? p.on_pos : !p.on_pos))
      && (!coll || p.collections.includes(coll))
      && (!qq || p.title.toLowerCase().includes(qq) || p.variants.some((v) => v.sku.toLowerCase().includes(qq))));
  }, [products, tab, q, coll]);

  const select = (ids, on) => { setSel((s) => { const n = new Set(s); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); return n; }); setAsking(null); };
  const chosen = products.filter((p) => sel.has(p.id));
  const toAdd = chosen.filter((p) => !p.on_pos);
  const toRemove = chosen.filter((p) => p.on_pos);
  const pickedShown = shown.filter((p) => sel.has(p.id)).length;

  const changeTab = (t) => { setTab(t); setSel(new Set()); setAsking(null); };

  const apply = async () => {
    const add = asking === 'add'; const list = add ? toAdd : toRemove;
    setBusy(true);
    try {
      const r = await gppApi.setPos(list.map((p) => p.id), add);
      const done = new Set(r.done_ids);
      setData((d) => ({ ...d, products: d.products.map((p) => (done.has(p.id) ? { ...p, on_pos: add } : p)) }));
      setSel((s) => { const n = new Set(s); done.forEach((id) => n.delete(id)); return n; });
      const ok = `${add ? 'Added' : 'Removed'} ${plural(r.done.length, 'product')} ${add ? 'to' : 'from'} the POS channel.`;
      setNotice(r.failed.length
        ? { tone: 'bad', text: `${ok} ${r.failed.length} failed: ${r.failed.map((f) => `${f.title} (${f.error})`).join('; ')}` }
        : { tone: 'ok', text: ok });
    } catch (e) {
      setNotice({ tone: 'bad', text: e.message });
    }
    setAsking(null); setBusy(false);
  };

  const onTransferred = (r) => {
    setXfer(null);
    if (r.product) setData((d) => ({ ...d, products: d.products.map((p) => (p.id === r.product.id ? r.product : p)) }));
    else load();
    setNotice({ tone: 'ok', text: `Moved ${plural(r.units, 'unit')} of ${r.title} to ${data.to} (${r.lines.map((l) => `${l.title} ${l.qty}`).join(', ')}).` });
  };

  const onPos = products.filter((p) => p.on_pos);
  const xferProduct = xfer && products.find((p) => p.id === xfer);

  return (
    <section className="uh-page uh-gpp">
      <div className="uh-ph">
        <div>
          <h1 className="uh-h1">Gotham FC POS Products</h1>
          <div className="uh-sub">Gotham FC Shop · Point of Sale channel · stock moves from {data ? data.from : 'Eatontown Warehouse (Gotham FC)'} to {data ? data.to : 'SI Stadium'}</div>
        </div>
        <button type="button" className="uh-btn" disabled={loading} onClick={load}>{loading && data ? 'Refreshing…' : 'Refresh from Shopify'}</button>
      </div>

      {data && (
        <div className="uh-ia-stats">
          {[[`${num(onPos.length)} of ${num(products.length)}`, 'products on POS'],
            [num(onPos.reduce((a, p) => a + Math.max(0, sum(p, 'stadium')), 0)), 'units at SI Stadium (POS products)'],
            [num(onPos.filter((p) => sum(p, 'stadium') <= 0).length), 'on POS, none at the stadium']]
            .map(([v, k]) => <div className="stat" key={k}><span className="v uh-num">{v}</span><span className="k">{k}</span></div>)}
        </div>
      )}
      {error && <div className="uh-kpc-banner bad">{error}</div>}
      {notice && <div className={`uh-kpc-banner ${notice.tone} uh-gpp-notice`}><span>{notice.text}</span><button type="button" className="uh-btn slim" onClick={() => setNotice(null)}>Dismiss</button></div>}
      {data && !data.can_write && <div className="uh-kpc-banner warn">This is a dev server: the list is live, but POS changes and transfers only run on the production Hub.</div>}

      <div className="uh-card">
        <div className="uh-card-h">
          <Seg value={tab} onChange={changeTab} />
          <div className="uh-search"><input type="search" placeholder="Search title or SKU" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search title or SKU" style={{ paddingLeft: 10 }} /></div>
          <select className="uh-select" value={coll} onChange={(e) => setColl(e.target.value)} aria-label="Collection">
            <option value="">All collections</option>
            {collections.map((c) => <option key={c}>{c}</option>)}
          </select>
          <span className="uh-sp" />
          {data && <span className="uh-gpp-hint">{num(shown.length)} shown</span>}
        </div>
        <div className="uh-tscroll"><table className="uh-tbl uh-gpp-tbl">
          <thead><tr>
            <th style={{ width: 44 }}>
              <input type="checkbox" aria-label="Select all shown" checked={shown.length > 0 && pickedShown === shown.length}
                ref={(el) => { if (el) el.indeterminate = pickedShown > 0 && pickedShown < shown.length; }}
                onChange={(e) => select(shown.map((p) => p.id), e.target.checked)} />
            </th>
            <th>Product</th><th>Created</th><th>POS</th><th className="r">Eatontown</th><th className="r">SI Stadium</th><th />
          </tr></thead>
          <tbody>
            {!data && !error && <tr><td colSpan={7}><div className="uh-empty">Loading products from the Gotham FC Shop…</div></td></tr>}
            {shown.map((p) => {
              const e = sum(p, 'eatontown'); const s = sum(p, 'stadium'); const can = p.variants.some((v) => avail(v) > 0);
              return (
                <tr key={p.id} className={sel.has(p.id) ? 'uh-tr-sel' : ''}>
                  <td><input type="checkbox" checked={sel.has(p.id)} aria-label={`Select ${p.title}`} onChange={(ev) => select([p.id], ev.target.checked)} /></td>
                  <td>
                    <div className="uh-gpp-prod">
                      <Thumb src={p.image} />
                      <div className="info">
                        <div className="nm">{p.title}</div>
                        <div className="sm">{[p.product_type, plural(p.variants.length, 'variant')].filter(Boolean).join(' · ')}</div>
                        <div className="uh-gpp-chips">
                          {p.collections.length ? p.collections.map((c) => <span key={c} className="chip">{c}</span>) : <span className="chip none">No collections</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(p.created_at)}</td>
                  <td><span className={`uh-gpp-pill ${p.on_pos ? 'on' : 'off'}`}>{p.on_pos ? 'On POS' : 'Off POS'}</span></td>
                  <td className={`r uh-num${e < 0 ? ' uh-gpp-neg' : ''}`}>{num(e)}</td>
                  <td className="r uh-num">{num(s)}</td>
                  <td>
                    <button type="button" className="uh-btn slim" disabled={!can} onClick={() => setXfer(p.id)}>Transfer…</button>
                    {!can && <div className="uh-gpp-rowwhy">Can't transfer: Eatontown is at 0 or below for {p.variants.length > 1 ? 'every size' : 'this item'} ({num(e)})</div>}
                  </td>
                </tr>
              );
            })}
            {data && !shown.length && <tr><td colSpan={7}><div className="uh-empty">No products match.</div></td></tr>}
          </tbody>
        </table></div>
      </div>

      {chosen.length > 0 && (
        <div className="uh-gpp-bar">
          {asking ? (
            <>
              <div className="uh-kpc-banner warn msg">
                {asking === 'add'
                  ? <>Add <b>{plural(toAdd.length, 'product')}</b> to the POS channel? They'll show up in the POS app.</>
                  : <>Remove <b>{plural(toRemove.length, 'product')}</b> from the POS channel? They'll stop showing in the POS app. Their stock counts don't change.</>}
                <div className="names">{names(asking === 'add' ? toAdd : toRemove)}</div>
              </div>
              <div className="btns">
                <button type="button" className="uh-btn" disabled={busy} onClick={() => setAsking(null)}>Back</button>
                <button type="button" className={`uh-btn ${asking === 'add' ? 'primary' : 'danger'}`} disabled={busy} onClick={apply}>
                  {busy ? 'Saving…' : asking === 'add' ? `Add ${plural(toAdd.length, 'product')}` : `Remove ${plural(toRemove.length, 'product')}`}
                </button>
              </div>
            </>
          ) : (
            <>
              <span><b>{plural(chosen.length, 'product')}</b> selected</span>
              <div className="btns">
                <button type="button" className="uh-btn" onClick={() => { setSel(new Set()); setAsking(null); }}>Clear selection</button>
                {toAdd.length > 0 && <button type="button" className="uh-btn primary" disabled={!data.can_write} onClick={() => setAsking('add')}>Add {num(toAdd.length)} to POS</button>}
                {toRemove.length > 0 && <button type="button" className="uh-btn danger" disabled={!data.can_write} onClick={() => setAsking('rem')}>Remove {num(toRemove.length)} from POS</button>}
              </div>
            </>
          )}
        </div>
      )}

      {xferProduct && (
        <Transfer product={xferProduct} from={data.from} to={data.to} canWrite={data.can_write}
          onClose={() => setXfer(null)} onDone={onTransferred} />
      )}
    </section>
  );
}
